import { sessionExpired, showGenericError } from '../actions';
import app from '../app.json';
import { validateExpirationToken, serializeForUri } from './utils';
import i18n from '../i18n';

const BASE_IP = `${process.env.REACT_APP_API_URL}`;
const CLIENT_NAME = 'Petcloud';

export const SUBSCRIPTION_KEY_HEADER = {
  'Ocp-Apim-Subscription-Key': process.env.REACT_APP_SUBSCRIPTION_KEY,
};

// Only for the APIM-hosted proxy; Orca management URLs must not get the key.
export function fetchOrcaProxy(path, options = {}) {
  return fetch(`${process.env.REACT_APP_ORCA_PROXY_API_URL}/${path}`, {
    ...options,
    headers: { ...SUBSCRIPTION_KEY_HEADER, ...options.headers },
  });
}

export function saveDataOnSession(key, data) {
  return sessionStorage.setItem(key, data);
}

export function saveData(key, data) {
  sessionStorage.setItem(key, data);
  return localStorage.setItem(key, data);
}

export function getData(key) {
  // TODO: localStorage not working on inner webview,
  //  workaround from sessionStorage
  return localStorage.getItem(key) || sessionStorage.getItem(key);
}

// only local storage
export const getDataFromLocalStorage = (key) => {
  const data = localStorage.getItem(key);
  return JSON.parse(data);
};

// only local storage
export const setDataToLocalStorage = (key, data) => {
  localStorage.setItem(key, data);
};

export async function getStorageOrDefault(key) {
  try {
    const data = await getData(key);

    return JSON.parse(data);
  } catch (_) {
    return {};
  }
}

export function removeData(key) {
  return localStorage.removeItem(key);
}

export function removeAllData() {
  sessionStorage.clear();
  return localStorage.clear();
}

export const X_API_KEY_ADMIN = { 'X-Api-Key': 'uLXybXt3jHCugGsr' };

async function getApiToken() {
  return getData('token');
}

async function call({
  anonymous = false,
  data,
  dispatch,
  extraHeaders = {},
  method,
  responseJson = true,
  returnObject = 'json',
  url = '',
  useGenericError = true,
}) {
  const token = anonymous
    ? 'api-key' : await getApiToken();
  const headers = {
    'Accept-Language': i18n.language,
    authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Client-Name': CLIENT_NAME,
    'X-Client-Version': app.version,
    ...SUBSCRIPTION_KEY_HEADER,
    ...extraHeaders,
  };

  if (!token) {
    return { devError: 'No token loaded', statusCode: 401, success: false };
  }

  let statusCode;

  try {
    const requestData = {
      body: data && JSON.stringify(data),
      headers,
      method,
    };

    const tokenExpired = validateExpirationToken(token);

    if (!anonymous && tokenExpired) {
      dispatch(sessionExpired());

      return {
        data: { IsValid: false },
        devError: 'Token expired',
        success: false,
      };
    }

    const response = await fetch(`${BASE_IP}/${url}`, requestData);
    statusCode = response.status;

    if (response.status >= 500) {
      const error = await response.json();

      if (useGenericError
        && dispatch
        && returnObject === 'json'
        && !error.detail) {
        dispatch(showGenericError(true));
      }

      return { error, statusCode, success: false };
    }

    if (response.status === 204) {
      return { statusCode, success: true };
    }

    let returnableData = {};
    if (returnObject === 'blob') {
      const res = await response.blob();
      returnableData = { blob: res };
    } else {
      const res = responseJson ? await response.json()
        : { data: await response.text() };
      returnableData = res;

      if (returnableData instanceof Array) {
        return { data: returnableData, statusCode, success: true };
      }
    }

    return { success: response.ok, ...returnableData };
  } catch (error) {
    if (useGenericError && dispatch) {
      dispatch(showGenericError(true));
    }
    return {
      error: error?.message,
      statusCode: statusCode || 500,
      success: false,
    };
  }
}

export function get(dispatch, URL, params = {}, extraHeaders) {
  const queryParams = serializeForUri(params);
  const url = `${URL}?${queryParams}`;

  return call({ dispatch, extraHeaders, method: 'GET', url });
}

export function post(
  dispatch,
  url,
  data,
  returnObject = 'json',
  useGenericError = true,
) {
  return call({
    data,
    dispatch,
    method: 'POST',
    returnObject,
    url,
    useGenericError,
  });
}

export function postBlob(dispatch, url, data) {
  return call({ data, dispatch, method: 'POST', returnObject: 'blob', url });
}

export function put(dispatch, url, data) {
  return call({ data, dispatch, method: 'PUT', url });
}

export function del(dispatch, url, data) {
  return call({ data, dispatch, method: 'DELETE', url });
}

export function patch(
  dispatch,
  url,
  data,
  responseJson = false,
) {
  return call({
    data,
    dispatch,
    method: 'PATCH',
    responseJson,
    url,
  });
}

export async function apiCallWithApiKey(
  path,
  params = {},
  dispatch,
  useGenericError = true,
) {
  try {
    let url = `${BASE_IP}/${path}`;

    const options = {
      headers: {
        'Accept-Language': i18n.language,
        Authorization: 'APIKEY 8f7ecbb6-a62c-44d9-ab88-7b295ba7956c',
        'Content-Type': 'application/json',
        'X-Client-Name': CLIENT_NAME,
        'X-Client-Version': app.version,
        ...SUBSCRIPTION_KEY_HEADER,
        ...params.headers,
      },
    };

    if (params.params) {
      const queryParams = serializeForUri(params.params);
      url += `?${queryParams}`;
    }

    const bodyVerbs = ['POST', 'DELETE', 'PATCH'];

    if (params.method && bodyVerbs.includes(params.method)) {
      options.method = params.method;
      options.body = JSON.stringify(params.body);
    }

    const response = await fetch(url, options);

    if (response.status === 204) {
      // avoid crashing when trying to parse empty (but valid) response
      return { status: response.status, success: true };
    }

    const res = await response.json();
    const status = response.status;

    if (response.status >= 500) {
      if (dispatch && useGenericError) {
        dispatch(showGenericError(true));
      }
      return {
        ...res,
        data: res,
        status,
        success: false,
      };
    }

    return {
      ...res,
      // needed in a separated property for responses like arrays direct
      // in the root (for avoiding spreadding the array with
      // success and status properties)
      response: res,
      status,
      success: response.ok,
    };
  } catch (_) {
    if (dispatch) {
      dispatch(showGenericError(true));
    }
    return {
      success: false,
    };
  }
}

export function getAnonymous(
  dispatch,
  URL,
  params = {},
  headers = {},
) {
  const queryParams = serializeForUri(params);
  let url = URL;
  if (queryParams) {
    // concats only the query params when they actully exists...
    // avoiding adding a :question: character at the end
    // causing problems with new apis (:
    // this fix may be applied to the rest of get requests...
    url = `${URL}?${queryParams}`;
  }

  return call({
    anonymous: true,
    dispatch,
    extraHeaders: headers,
    method: 'GET',
    url,
  });
}

export function postAnonymous(
  dispatch,
  URL,
  body,
  params = {},
) {
  const queryParams = serializeForUri(params);
  const url = `${URL}?${queryParams}`;

  return call({
    anonymous: true,
    data: body,
    dispatch,
    method: 'POST',
    url,
  });
}
